import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Mail, Send, Sparkles } from "lucide-react";
import { surveysAdmin } from "../../../lib/adminQueues";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
const sel = "rounded-md border border-border bg-bone px-3 py-1.5 text-sm font-semibold text-ink focus:border-ink focus:outline-none";

/** Weekly summary schedule, AI themes, recipients, immediate-alert words. */
export const DigestSettings = ({ config, onSave }) => {
  const [weekday, setWeekday] = useState(config.digest.weekday);
  const [hour, setHour] = useState(config.digest.hour);
  const [ai, setAi] = useState(config.digest.ai_themes);
  const [extras, setExtras] = useState(config.digest.extra_recipients.join(", "));
  const [alertsOn, setAlertsOn] = useState(config.alerts.enabled);
  const [words, setWords] = useState(config.alerts.problem_words.join(", "));
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setWeekday(config.digest.weekday); setHour(config.digest.hour); setAi(config.digest.ai_themes);
    setExtras(config.digest.extra_recipients.join(", ")); setAlertsOn(config.alerts.enabled); setWords(config.alerts.problem_words.join(", "));
  }, [config]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        digest: { weekday, hour, ai_themes: ai, extra_recipients: extras.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean) },
        alerts: { enabled: alertsOn, problem_words: words.split(/[,\n]+/).map((w) => w.trim()).filter(Boolean) },
      });
      toast.success("Summary settings saved.", { description: `${DAYS[weekday]}s at ${fmtHour(hour)} Pacific.` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings.");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await surveysAdmin.sendDigestNow();
      if (r.sent) toast.success(`Summary sent to ${r.to}.`, { description: `${r.count} response${r.count === 1 ? "" : "s"}${r.themes ? " with AI themes" : ""}.` });
      else toast("Nothing new since the last summary.", { description: "No email was sent." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the summary.");
    } finally {
      setSending(false);
    }
  };

  const last = config.digest.last_digest_sent_at;
  return (
    <div className="space-y-5" data-testid="feedback-digest-settings">
      <div className="flex flex-wrap items-center gap-3 text-sm text-ink">
        <span>Send the weekly summary every</span>
        <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} data-testid="feedback-digest-weekday" className={sel}>
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
        <span>at</span>
        <select value={hour} onChange={(e) => setHour(Number(e.target.value))} data-testid="feedback-digest-hour" className={sel}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
        </select>
        <span className="text-olive">Pacific time. Quiet weeks send nothing.</span>
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-ink/10 bg-sand px-4 py-3">
        <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} data-testid="feedback-digest-ai" className="mt-1" />
        <span className="text-sm text-ink"><span className="inline-flex items-center gap-1.5 font-semibold"><Sparkles className="h-4 w-4 text-tangerine" /> AI "themes this week"</span> at the top of the summary: three short bullets drawn from the scores and comments, plus a "Watch" line if something sounds like a bug or a trust problem.</span>
      </label>

      <label className="block">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">Who receives summaries and alerts</span>
        <span className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-[13px] font-semibold text-sand" data-testid="feedback-primary-recipient"><Mail className="h-3.5 w-3.5" /> {config.primary_recipient}</span>
          <input value={extras} onChange={(e) => setExtras(e.target.value)} placeholder="Board members who opted in: name@example.org, …" data-testid="feedback-extra-recipients" className="min-w-[260px] flex-1 rounded-md border border-border bg-sand px-3 py-2 text-sm text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none" />
        </span>
        <span className="mt-1 block text-[12px] text-olive">Up to {config.max_extra_recipients} board members, separated by commas. Separate from the wall-alert list, so a board member can get one and not the other.</span>
      </label>

      <label className="block">
        <span className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">
          Immediate alert
          <span className="inline-flex items-center gap-1.5 normal-case tracking-normal"><input type="checkbox" checked={alertsOn} onChange={(e) => setAlertsOn(e.target.checked)} data-testid="feedback-alerts-enabled" /> on</span>
        </span>
        <textarea value={words} onChange={(e) => setWords(e.target.value)} rows={3} data-testid="feedback-problem-words" className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2 font-mono text-[13px] leading-relaxed text-ink focus:border-ink focus:outline-none" />
        <span className="mt-1 block text-[12px] text-olive">Any score of 1 or 2 always alerts. A comment containing one of these words (comma-separated, matched anywhere, not case-sensitive) alerts too.</span>
      </label>

      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={save} disabled={busy} data-testid="feedback-digest-save" className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand hover:bg-forest disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings</button>
        <button type="button" onClick={sendNow} disabled={sending} data-testid="feedback-digest-send" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-ink hover:text-sand disabled:opacity-50">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send summary now</button>
        <button type="button" onClick={() => surveysAdmin.exportCsv().catch(() => toast.error("Export failed."))} data-testid="feedback-export" className="inline-flex items-center gap-2 text-sm font-semibold text-ink underline-offset-2 hover:underline"><Download className="h-4 w-4" /> Export all responses (CSV)</button>
        <span className="text-[12px] text-olive" data-testid="feedback-digest-status">
          {last ? `Last summary ${new Date(last).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · ${config.digest.last_digest_count ?? 0} response${config.digest.last_digest_count === 1 ? "" : "s"}` : "No summary sent yet"}
        </span>
      </div>
    </div>
  );
};
